import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-navy-ink blueprint-grid flex items-center justify-center px-6 pt-20" data-testid="admin-login-page">
      <form onSubmit={handleSubmit} className="w-full max-w-md bg-white p-10 md:p-12 shadow-2xl" data-testid="admin-login-form">
        <div className="flex items-center gap-3 mb-2">
          <span className="w-10 h-10 bg-navy-ink text-brand flex items-center justify-center"><Lock size={18} /></span>
          <h1 className="font-display uppercase text-navy text-2xl" data-testid="admin-login-heading">BuilderEDU Admin</h1>
        </div>
        <p className="text-slate-500 text-sm mb-8">Sign in to view contact submissions.</p>

        {error && (
          <p className="mb-6 text-sm font-semibold text-red-600 bg-red-50 border border-red-200 px-4 py-3" data-testid="admin-login-error">{error}</p>
        )}

        <div className="flex flex-col gap-2 mb-6">
          <Label htmlFor="admin-email" className="font-head text-xs font-bold uppercase tracking-[0.15em] text-navy">Email</Label>
          <Input id="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="bg-slate-50 rounded-sm h-12 focus-visible:ring-brand" data-testid="admin-email-input" />
        </div>
        <div className="flex flex-col gap-2 mb-8">
          <Label htmlFor="admin-password" className="font-head text-xs font-bold uppercase tracking-[0.15em] text-navy">Password</Label>
          <Input id="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            className="bg-slate-50 rounded-sm h-12 focus-visible:ring-brand" data-testid="admin-password-input" />
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="w-full font-head font-bold uppercase tracking-[0.12em] text-sm bg-brand text-navy-ink px-8 py-4 inline-flex items-center justify-center gap-3 hover:bg-navy-ink hover:text-white transition-colors duration-300 disabled:opacity-60"
        >
          {loading && <Loader2 size={18} className="animate-spin" />}
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </form>
    </main>
  );
}
